const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const NBSP = "\u00a0";
export const TIME_OF_DAY: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/g;
const HIDDEN_FORMAT_CHARS = /[\u061C\u200B\u200E\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/gu;

// Every process formats with the one locale its host sets, since main and a page can each default to a different one.
let displayLocale: string | undefined;
// Building a date formatter is slow and a long list formats every row, so each locale and style is built once.
const formatters = new Map<string, Intl.DateTimeFormat>();

/** Bytes → binary (1024) human string: B / KB / MB. Recording file sizes. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function cleanDisplayText(text: string, maxLength = Infinity): string {
  return text.replace(CONTROL_CHARS, " ").replace(HIDDEN_FORMAT_CHARS, "").replace(/\s+/g, " ").trim().slice(0, maxLength);
}

/** Sets the locale every date and time formatter here uses; undefined falls back to the runtime default. */
export function setDisplayLocale(locale: string | undefined): void {
  displayLocale = locale;
}

export function getDisplayLocale(): string | undefined {
  return displayLocale;
}

export function formatterFor(locale: string | undefined, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale ?? ""}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key);
  if (!formatter) formatters.set(key, (formatter = new Intl.DateTimeFormat(locale, options)));
  return formatter;
}

/** Formatted parts → one string with the 12-hour day period written "AM" or "PM" and every space a plain one, whatever the locale wrote. */
export function withPlainDayPeriod(parts: Intl.DateTimeFormatPart[], hour: number): string {
  return parts
    .map((part, index) => {
      // Spaces become plain ones because a saved take name has its spaces collapsed to plain ones, and the two must compare equal.
      if (part.type === "literal") return part.value.replace(/\s/g, " ");
      if (part.type !== "dayPeriod") return part.value;
      const period = hour < 12 ? "AM" : "PM";
      // Locales such as zh write the period against the hour, which reads as one word once it is Latin.
      const before = index > 0 && parts[index - 1].type !== "literal" ? " " : "";
      const after = index < parts.length - 1 && parts[index + 1].type !== "literal" ? " " : "";
      return `${before}${period}${after}`;
    })
    .join("");
}

/** Epoch ms → the hour and minute in the user's own clock style (9:05 PM, 21:05), with the day period always "AM"/"PM". */
export function formatTimeOfDay(ms: number, locale: string | undefined = displayLocale): string {
  const date = new Date(ms);
  return withPlainDayPeriod(formatterFor(locale, TIME_OF_DAY).formatToParts(date), date.getHours());
}

/** Epoch ms → the short day the locale's own way ("Oct 4", "4 Oct"), the year too when `withYear`; an undefined locale is the runtime default, not the display locale. */
export function formatShortDay(ms: number, withYear: boolean, locale: string | undefined): string {
  return formatterFor(locale, withYear ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" }).format(new Date(ms)).replace(/\s/g, " ");
}

/** Epoch ms → the short day, with the year only when it is not the year of `now`. */
export function formatDay(ms: number, now: number = Date.now(), locale: string | undefined = displayLocale): string {
  return formatShortDay(ms, new Date(ms).getFullYear() !== new Date(now).getFullYear(), locale);
}

/** Epoch ms → the short day and the time of day ("Sep 25, 9:05 PM"), the year too when `withYear`. */
export function formatDayAndTime(ms: number, withYear = false, locale: string | undefined = displayLocale): string {
  // Joined by hand: newer ICU data (iOS) writes "Oct 19 at 3:45 PM" when date and time are formatted together.
  return `${formatShortDay(ms, withYear, locale)}, ${formatTimeOfDay(ms, locale)}`;
}

/** Epoch ms → the day and time, with the year only when it is not the year of `now`. */
export function formatTimestamp(ms: number, now: number = Date.now(), locale: string | undefined = displayLocale): string {
  return formatDayAndTime(ms, new Date(ms).getFullYear() !== new Date(now).getFullYear(), locale);
}

/** Seconds → words joined by non-breaking spaces so they never wrap apart: `50 sec`, `42 min`, `1 h 6 min`. Rounds to the nearest second, then to whole minutes from a minute up. */
export function formatDurationWords(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(Number.isFinite(totalSeconds) ? totalSeconds : 0));
  if (seconds < SECONDS_PER_MINUTE) return `${seconds}${NBSP}sec`;
  const minutes = Math.round(seconds / SECONDS_PER_MINUTE);
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const rest = minutes % MINUTES_PER_HOUR;
  if (hours === 0) return `${minutes}${NBSP}min`;
  const hourText = hours.toLocaleString("en-US");
  return rest === 0 ? `${hourText}${NBSP}h` : `${hourText}${NBSP}h${NBSP}${rest}${NBSP}min`;
}
